import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext";
import { CartProvider } from "./contexts/CartContext";
import { AdminProvider, useAdmin } from "./contexts/AdminContext";
import { ClientProvider, useClient } from "./contexts/ClientContext";
import { Toaster } from "./components/ui/sonner";
import Header from "./components/Header";
import Hero from "./components/Hero";
import Services from "./components/Services";
import Portfolio from "./components/Portfolio";
import About from "./components/About";
import Testimonials from "./components/Testimonials";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import Cart from "./components/Cart";
import WhatsAppButton from "./components/WhatsAppButton";
import Checkout from "./components/Checkout";
import PaymentSuccess from "./components/PaymentSuccess";
import PaymentCancel from "./components/PaymentCancel";
import SEOHead from "./components/SEOHead";

// Admin Components
import AdminLogin from "./components/admin/AdminLogin";
import AdminLayout from "./components/admin/AdminLayout";
import AdminDashboard from "./components/admin/AdminDashboard";
import OrdersManagement from "./components/admin/OrdersManagement";
import ServicesManagement from "./components/admin/ServicesManagement";
import PortfolioManagement from "./components/admin/PortfolioManagement";
import TestimonialsManagement from "./components/admin/TestimonialsManagement";
import MessagesManagement from "./components/admin/MessagesManagement";
import UsersManagement from "./components/admin/UsersManagement";
import SettingsManagement from "./components/admin/SettingsManagement";
import InvoicesManagement from "./components/admin/InvoicesManagement";

// Client Components
import ClientAuth from "./components/client/ClientAuth";
import ClientDashboard from "./components/client/ClientDashboard";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAdmin();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#141619]">
      <div className="text-[#D4AF37] text-xl">Loading...</div>
    </div>;
  }
  
  return isAuthenticated ? children : <Navigate to="/admin" replace />;
};

// Main Website
const MainWebsite = () => (
  <>
    <SEOHead />
    <Header />
    <main>
      <Hero />
      <Services />
      <Portfolio />
      <About />
      <Testimonials />
      <Contact />
    </main>
    <Footer />
    <Cart />
    <WhatsAppButton />
  </>
);

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <CartProvider>
          <AdminProvider>
            <ClientProvider>
              <div className="App">
                <Routes>
                  {/* Main Website Route */}
                  <Route path="/" element={<MainWebsite />} />
                  
                  {/* Checkout Routes */}
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/payment-success" element={<PaymentSuccess />} />
                  <Route path="/payment-cancel" element={<PaymentCancel />} />
                  
                  {/* Client Portal Routes */}
                  <Route path="/client/login" element={<ClientAuth />} />
                  <Route path="/my-account" element={<ClientDashboard />} />
                  
                  {/* Admin Login Route */}
                  <Route path="/admin" element={<AdminLogin />} />
                  
                  {/* Protected Admin Routes */}
                  <Route path="/admin/*" element={
                    <ProtectedRoute>
                      <AdminLayout />
                    </ProtectedRoute>
                  }>
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="orders" element={<OrdersManagement />} />
                    <Route path="services" element={<ServicesManagement />} />
                    <Route path="portfolio" element={<PortfolioManagement />} />
                    <Route path="testimonials" element={<TestimonialsManagement />} />
                    <Route path="messages" element={<MessagesManagement />} />
                    <Route path="invoices" element={<InvoicesManagement />} />
                    <Route path="users" element={<UsersManagement />} />
                    <Route path="settings" element={<SettingsManagement />} />
                  </Route>
                </Routes>
                <Toaster position="top-right" />
              </div>
            </ClientProvider>
          </AdminProvider>
        </CartProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
